/**
 * Build-version identity for the running JS bundle.
 *
 * `BUILD_ID` / `BUILD_TIME` are injected at build time via Vite's `define`
 * (see vite.config.ts). The same values are written to `/version.json`
 * inside the deployed `dist/`, which lets a running client compare its
 * embedded build id with the latest deployed one.
 */

declare const __BUILD_ID__: string;
declare const __BUILD_TIME__: string;

export const BUILD_ID: string =
  typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";
export const BUILD_TIME: string =
  typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "";

export const SHORT_BUILD_ID = BUILD_ID.slice(0, 8);

export interface RemoteVersion {
  buildId: string;
  builtAt: string;
}

export type VersionStatus = "checking" | "current" | "outdated" | "unknown";

/**
 * Fetch the latest deployed version from `/version.json`.
 *
 * Cache-busted via query string + `cache: 'no-store'` + `Cache-Control`
 * header so intermediate caches (CDN, service worker, browser disk cache)
 * cannot return a stale manifest — defeats the cache-busting purpose
 * otherwise.
 */
export async function fetchRemoteVersion(): Promise<RemoteVersion | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<RemoteVersion>;
    if (!data || typeof data.buildId !== "string") return null;
    return {
      buildId: data.buildId,
      builtAt: typeof data.builtAt === "string" ? data.builtAt : "",
    };
  } catch {
    return null;
  }
}

// Expose for ad-hoc inspection from DevTools.
if (typeof window !== "undefined") {
  (window as unknown as { __LOVABLE_BUILD__?: unknown }).__LOVABLE_BUILD__ = {
    buildId: BUILD_ID,
    builtAt: BUILD_TIME,
    check: fetchRemoteVersion,
  };
}